import { useState } from 'react';
import type { ChangeEvent, DragEvent, ReactNode } from 'react';
import { Badge } from '../../design-system';

export type UploadKind = 'mtc' | 'onderhoud';

const ACCEPT: Record<UploadKind, string> = {
  mtc: 'application/pdf,.pdf,.PDF',
  onderhoud: 'application/pdf,.pdf,.PDF',
};

function UploadCard({
  kind,
  title,
  badge,
  description,
  hint,
  busyLabel,
  busy,
  disabled,
  disabledNote,
  onFile,
}: {
  kind: UploadKind;
  title: string;
  badge: ReactNode;
  description: ReactNode;
  hint: string;
  busyLabel: string;
  /** Which upload is running page-wide (locks all cards). */
  busy: UploadKind | null;
  disabled?: boolean;
  disabledNote?: ReactNode;
  onFile: (kind: UploadKind, file: File) => void;
}) {
  const [dragOver, setDragOver] = useState(false);
  const isBusy = busy === kind;
  const locked = disabled || busy !== null;

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) onFile(kind, file);
  }
  function handleDrop(e: DragEvent) {
    e.preventDefault();
    setDragOver(false);
    if (locked) return;
    const file = e.dataTransfer.files?.[0];
    if (file) onFile(kind, file);
  }

  return (
    <label
      onDragOver={(e) => {
        e.preventDefault();
        if (!locked) setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: '18px 20px',
        background: dragOver ? '#EFF4F8' : '#FFFFFF',
        border: dragOver ? '2px dashed #2A6085' : '1px solid #E3D9CC',
        borderRadius: 4,
        cursor: locked ? 'default' : 'pointer',
        opacity: disabled || (busy !== null && !isBusy) ? 0.55 : 1,
        transition: 'background 150ms ease, border-color 150ms ease',
      }}
    >
      <input
        type="file"
        accept={ACCEPT[kind]}
        disabled={locked}
        onChange={handleChange}
        style={{ display: 'none' }}
      />
      <div
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}
      >
        <span style={{ fontSize: 15, fontWeight: 900, color: '#111111' }}>{title}</span>
        {badge}
      </div>
      <div style={{ fontSize: 13, color: '#666666', lineHeight: 1.5 }}>{description}</div>
      {disabled && disabledNote ? (
        <div style={{ fontSize: 12, color: '#B8801E', fontWeight: 700 }}>{disabledNote}</div>
      ) : (
        <div
          style={{
            fontSize: 12,
            color: isBusy ? '#2A6085' : '#8C8C82',
            fontWeight: isBusy ? 700 : 400,
          }}
        >
          {isBusy ? busyLabel : hint}
        </div>
      )}
    </label>
  );
}

/**
 * The two import routes: the fuel-card report (deterministic, no AI) and the
 * maintenance invoice (needs an AI Gateway on this environment).
 *
 * The first card covers both reports MTC's system puts out — the
 * `Transactieoverzicht` you export by hand and MoveMove's monthly
 * `Factuurbijlage` — because from where the user stands that is one job: the
 * fuel-card bill goes in here. Which of the two it is gets decided from the
 * document itself, so making the user pick would only be a way to get it wrong.
 * The `kind` stays `mtc`: that is the API's invoice type, not a format.
 */
export function UploadCards({
  busy,
  aiBeschikbaar,
  onFile,
}: {
  busy: UploadKind | null;
  /** undefined while settings load — treat as available to avoid flicker. */
  aiBeschikbaar: boolean | undefined;
  onFile: (kind: UploadKind, file: File) => void;
}) {
  const aiUit = aiBeschikbaar === false;
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
        gap: 12,
      }}
    >
      <UploadCard
        kind="mtc"
        title="Tankpasfactuur"
        badge={null}
        description={
          <>
            Beide formaten van MTC en MoveMove: het <strong>transactieoverzicht</strong> dat je zelf
            van het platform haalt, en de <strong>factuurbijlage</strong> die maandelijks per mail
            komt. Elke regel wordt ingelezen en gecontroleerd tegen de totalen in het document zelf,
            dus er komt niets binnen dat niet klopt. Passen worden automatisch aangemaakt.
          </>
        }
        hint="Klik of sleep het PDF-bestand hierheen"
        busyLabel="Lezen en controleren…"
        busy={busy}
        onFile={onFile}
      />
      <UploadCard
        kind="onderhoud"
        title="Onderhoudsfactuur"
        badge={
          aiUit ? <Badge tone="warning">AI niet ingesteld</Badge> : <Badge tone="blue">AI</Badge>
        }
        description={
          <>
            Garagefactuur (PDF). AI leest kenteken, garage, soort beurt en bedrag; jij controleert
            het resultaat voordat het wordt opgeslagen.
          </>
        }
        hint="Klik of sleep het PDF-bestand hierheen"
        busyLabel="AI analyseert de factuur…"
        busy={busy}
        disabled={aiUit}
        disabledNote="AI Gateway is niet ingesteld op deze omgeving. Dat staat in de Cloudflare-configuratie van de Worker, niet in Instellingen."
        onFile={onFile}
      />
    </div>
  );
}
